<template>
  <HeadlessTransitionRoot
    appear
    :show="isOpen"
    as="template"
  >
    <HeadlessDialog
      as="div"
      class="relative z-50"
      @close="close"
    >

      <HeadlessTransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black bg-opacity-25">
          <MouseFollower :hide-when-over-el="dialogPanelEl" />
        </div>
      </HeadlessTransitionChild>

      <div class="fixed bottom-0 left-0 top-0 w-screen overflow-y-auto overflow-x-hidden">
        <div class="min-h-full">
          <HeadlessTransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 translate-x-full"
            enter-to="opacity-100 translate-x-0"
            leave="duration-200 -mr-[17px] ease-in"
            leave-from="opacity-100 translate-x-0"
            leave-to="opacity-0 translate-x-full"
          >
            <HeadlessDialogPanel class="absolute right-0 h-full w-full max-w-3xl bg-whitegray shadow-xl transition-all">

              <MessageDialog
                :message="errorMessage"
                :show="showNotDeliveringMessage"
                @close="showNotDeliveringMessage = false"
              />

              <MessageDialog
                :message="errorMessage"
                :show="showErrorMessage"
                @close="showErrorMessage = false"
              />

                <PriceChangedDialog :is-open="showPriceChangedDialog" :delivery-price="deliveryPrice" @proceed-checkout="proceedCheckout" @close="showPriceChangedDialog = false"></PriceChangedDialog>

              <form
                ref="dialogPanelEl"
                class="h-full w-full"
                @submit.prevent="onSubmit"
              >

                  <Transition
                  name="fade"
                  mode="out-in"
                >
                  <OrderDialogCart
                    v-if="stage == 'cart'"
                    @cart-items-updated="onCartItemsUpdated"
                    @close="close"
                    @procced-to-payment="stage = 'payment'"
                  />
                  <OrderDialogPayment
                    v-else-if="stage == 'payment'"
                    :basket="basket"
                    @close="close"
                    :is-form-submit-disabled="isFormSubmitDisabled"
                    @back-to-cart="stage = 'cart'"
                  />
                  <OrderDialogThanksForOrder v-else-if="stage == 'thanks'" @close="close" :is-paid="newOrderIsPaid"  />
                  <OrderDialogRestClosed v-else-if="stage == 'rest_closed'" />
                  <OrderDialogCantDeliver v-else-if="stage == 'cant_deliver'" />
                </Transition>
              </form>
            </HeadlessDialogPanel>
          </HeadlessTransitionChild>
        </div>
      </div>
    </HeadlessDialog>
  </HeadlessTransitionRoot>
</template>

<script setup lang="ts">
import { DateTime } from 'luxon'
import { ref, toRefs } from 'vue'
import * as yup from 'yup'
import { formatPhone } from '~/utils'
import { type GetBasketResponse, useInvalidateBasket } from '~/composables/api/basket'
import MyToastError from '~/components/MyToastError.vue'
import { useToast } from 'vue-toastification'
import { useCartDialogStore } from '~/store/cartDialog'
import { storeToRefs } from 'pinia'
import PriceChangedDialog from '~/components/Order/Dialog/PriceChangedDialog.vue'

const dialogPanelEl = ref()
const basket = ref<GetBasketResponse>()

let errorMessage = "Доставка на этот адрес не осуществляется. \n Адрес вне зоны доставки 😞 Ждём вас в суши-баре!"
const stage = ref<'cart' | 'payment' | 'thanks' | 'rest_closed' | 'cant_deliver'>('cart')

let isFormSubmitDisabled = ref(false)
let totalSum = 0;
let deliveryPrice = ref(0)
let showPriceChangedDialog = ref(false)

const { value: loyaltyProgramId } = useField<string>('loyalty_program_id')
const {value: coupon} = useField<string>('coupon')

const { data: basketData, refetch: refetchBasket } = useBasketWithGifts((v) => {
    return v
}, { enabled: true, coupon, loyalty_program_id: loyaltyProgramId })


const onCartItemsUpdated = (b: GetBasketResponse) => {
    totalSum = b.total_price
    basket.value = b

    deliveryPrice.value = b.delivery_price
    showNotDeliveringMessage.value = !b.is_delivery_available;
}
const validationSchema = computed(() => {
  if (stage.value === 'cart') {
    return yup.object({
      coupon: yup.string().label('Промокод'),
      loyalty_program_id: yup.string().label('Программа лояльности'),
      guests_count: yup.number().min(1).label('Количество гостей'),
    })
  } else if (stage.value === 'payment') {
    return yup.object({
      name: yup.string().label('Имя'),
      phone: yup.string().label('Телефон'),
      leave_under_door: yup.boolean().label('Оставить у двери'),
      payment_type: yup.string().required("Пожалуйста, выберите тип оплаты"),
      //comment: yup.string().required().min(3).label('Комментарий к заказу'),
      comment: yup.string().label('Комментарий к заказу'),
      cashback: yup
        .number()
        .label('Сдача'),
    })
  }
})

const { handleSubmit, resetForm, setFieldValue } = useForm<any>({
  validationSchema,
  initialValues: computed(() => ({
    time_deliver: 'soon',
    guests_count: 0,
    leave_under_door: false,
    payment_type: 'cash',
    save_card: false,
  })),
  keepValuesOnUnmount: true,
})
const toast = useToast()
function onInvalidSubmit({ values, errors, results }) {
    if(errors) {
        toast({
            component: MyToastError,
            props: {
                title: 'Ошибка',
                detail: Object.values(errors).join(" \n"),
            },
        })
    }
}

const { data: user } = useUser((v) => v)

watch(
  [user],
  () => {
      /*if (user.value) {
          resetForm({
              values: {
                  name: user.value.name,
                  phone: formatPhone(user.value.phone || ''),
              },
          })
      }*/

    if (user.value) {
        setFieldValue("name", user.value.name)
        setFieldValue("phone", formatPhone(user.value.phone || ''))
    }
  },
  {
    immediate: true,
  }
)

const { mutateAsync } = useCreateOrder()
const invalidateOrders = useInvalidateOrders()
const invalidateBasket = useInvalidateBasket()

const showNotDeliveringMessage = ref(false)
const showErrorMessage = ref(false)

const cartDialogStore = useCartDialogStore()
const { isOpen } = storeToRefs(cartDialogStore)

const close = () => {
    stage.value = 'cart'

    cartDialogStore.close()
}

const newOrderIsPaid = ref(false)

const proceedCheckout = () => {
    showPriceChangedDialog.value = false
    onSubmit()
}

const onSubmit = handleSubmit (async (v: any) => {
    if (isFormSubmitDisabled.value) {
        return
    }
    isFormSubmitDisabled.value = true

    let resp = await refetchBasket()
    if (resp.data?.delivery_price) {
        let oldDeliveryPrice = deliveryPrice.value
        deliveryPrice.value = resp.data.delivery_price
        basket.value = resp.data
        if (oldDeliveryPrice < resp.data.delivery_price) {
            showPriceChangedDialog.value = true
            invalidateBasket()
            isFormSubmitDisabled.value = false
            return
        }
    }

    const ph: string = v.phone
    v.phone = ph.replace(/\D/g, '')

    if (!v.spend || v.spend.length == 0) {
        v.spend = 0
    }
    if (!v.need_change || v.need_change.length == 0) {
        v.need_change = 0
    }
    delete v.reception_way

    mutateAsync(v).then((response) => {
        try {
            let commerceProducts: any[] = [];
            basket.value?.items.forEach((item) => {
                commerceProducts.push({
                    "id": item.id,
                    "name": item.name,
                    "price": item.price,
                    "quantity": item.quantity
                });
            });

            if (commerceProducts.length > 0) {
                window.dataLayer.push({
                    "ecommerce": {
                        "currencyCode": "RUB",
                        "purchase": {
                            "actionField": {
                                "id": response.order_id
                            },
                            "products": commerceProducts
                        }
                    }
                });
            }

            /*ym(94201196, 'reachGoal', 'zakazfinish', {
                order_price: totalSum!.toFixed(2).toString(),
                currency: 'RUB',
            })*/
        } catch {
        }

        if (response.payment_confirmation_url.length > 0) {
            navigateTo(response.payment_confirmation_url, {
                external: true,
            })
        }
        newOrderIsPaid.value = response.is_paid
        stage.value = 'thanks'
        invalidateOrders()
        invalidateBasket()

        /*
        if (response.action === 'success') {
            stage.value = 'thanks'
            invalidateOrders()
            invalidateBasket()
        } else {
          if (response.action === 'not found address') {
            showNotDeliveringMessage.value = true
          }
          if (response.action === 'rest closed') {
            stage.value = 'rest_closed'
          }
          if (response.action === 'cant deliver') {
            stage.value = 'cant_deliver'
          }
        }*/
    }).catch((reason) => {
        errorMessage = "Ошибка оформления заказа"

        if (reason.response.data?.errors) {
            let message = ""

            reason.response.data?.errors.forEach((err: any) => {
                if (err.code == 42) {
                    stage.value = 'cart'
                    showErrorMessage.value = true
                }
                message += err.text + "\n"
            })

            if (message.length > 0) {
                errorMessage = "Не удалось оформить заказ\n" + message
            }
        }

        if (!showErrorMessage.value) {
            showNotDeliveringMessage.value = true
        }

    }).finally(() => {
        isFormSubmitDisabled.value = false
    })
}, onInvalidSubmit)

useSwipe(dialogPanelEl, {
  onSwipeEnd(_, direction) {
    if (direction === 'right') {
        close()
    }
  },
})
</script>
